"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQAccordionProps {
  faqs: FAQItem[];
}

/**
 * Single-column accordion. Receives FAQs as a prop from the server
 * component above it.
 *
 * Why single column (not the old 2-col grid): a CSS grid couples row
 * heights, so when one item expanded its empty pair stretched to match.
 * Stacking vertically lets each item open and close independently and
 * keeps the reading rhythm linear.
 *
 * Answer is rendered via dangerouslySetInnerHTML because the admin
 * authors it through Quill (HTML output). Same trust model as
 * PropertyShowcase + the blog body. Plain-text answers from the old
 * Textarea-era data still render fine — they just look like one block
 * until the admin re-edits them with paragraph breaks.
 */
const FAQAccordion = ({ faqs }: FAQAccordionProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) =>
    setActiveIndex(index === activeIndex ? null : index);

  return (
    <div className="flex flex-col gap-3">
      {faqs.map((faq, index) => {
        const isOpen = activeIndex === index;
        return (
          <div
            key={index}
            className="rounded-lg border border-[#B3DCFF] bg-[#E6F4FF] transition-colors"
          >
            <button
              type="button"
              onClick={() => toggleFAQ(index)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between gap-4 p-5 text-left"
            >
              <h3 className="text-[#120F30] font-medium text-base md:text-[19px] leading-snug">
                {faq.question}
              </h3>
              <ChevronDown
                aria-hidden="true"
                className={`h-5 w-5 flex-shrink-0 text-[#120F30] transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {/* Body: rendered as Quill HTML with explicit paragraph,
                list, and link spacing. whitespace-pre-line preserves any
                legacy plain-text \n breaks from before the editor
                upgrade so old FAQs still read cleanly. */}
            <div
              className={`grid transition-all duration-200 ease-out ${
                isOpen
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <div
                  className="px-5 pb-5 text-[#3A3A3A] text-[15px] md:text-[16px] leading-relaxed whitespace-pre-line
                    [&_p]:my-3
                    [&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-[#120F30]
                    [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-[#120F30]
                    [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5
                    [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1.5
                    [&_strong]:font-semibold [&_strong]:text-[#120F30]
                    [&_em]:italic
                    [&_a]:text-[#0094FF] [&_a]:underline [&_a]:underline-offset-2 [&_a]:break-words
                    [&_h2+p]:!mt-1 [&_h3+p]:!mt-1
                    [&>:first-child]:!mt-0 [&>:last-child]:!mb-0"
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FAQAccordion;
